import type {
  MobileFrontEndGalleryView,
  MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { ARTIFACT_ID_FIELD } from './constants';
import { artifactTableView } from './labels';
import { isSelectableFieldPath, resolvePath } from './message-mapping';
import { buildPagedQuery } from './selection-set';
import { displayableMediaUrl, resolveMediaKind, type GalleryMediaKind } from './media-kind';
import { orderByIfSortable } from './where';

export type { GalleryMediaKind } from './media-kind';
export { resolveMediaKind } from './media-kind';

export interface GalleryItem {
  id: string;
  /** Full-size asset URL. */
  url: string;
  /** Preview URL; falls back to the full-size asset. */
  thumbnailUrl: string;
  kind: GalleryMediaKind;
  caption: string | null;
  timestamp: string | null;
  row: Record<string, unknown>;
}

/**
 * MIME path for the same collected file as a ``.file.s3Url`` field.
 *
 * Attachment paths often end in ``.mp4`` or ``.heic`` while the stored bytes
 * are JPEG, so the extension cannot decide the player.
 */
export function collectedFileMimeField(urlField: string | null | undefined): string | null {
  const suffix = '.file.s3Url';
  if (!urlField?.endsWith(suffix)) return null;
  return `${urlField.slice(0, -suffix.length)}.file.mimeType`;
}

const ORIGINAL_MEDIA_FIELD = 'mediaCollectedFilePath.file.s3Url';
const ATTACHMENT_PREVIEW_FIELD = 'attachmentdataCollectedFilePath.file.s3Url';

/**
 * Use the attachment preview when this gallery only names the original file.
 *
 * Photos Media Information stores the original on ``media`` and a smaller JPEG
 * on ``attachmentdata``. Deployed ui-config still points the gallery at
 * ``media`` alone and declares every row an image. Once the registry publishes
 * ``thumbnail_field`` and ``media_kind: auto``, this leaves that view alone.
 */
export function withCollectedAttachmentPreview(
  view: MobileFrontEndGalleryView,
  artifact: MobileUiConfigArtifact | undefined,
): MobileFrontEndGalleryView {
  if (view.thumbnail_field || view.media_field !== ORIGINAL_MEDIA_FIELD) return view;
  const hasAttachment = artifact?.fields.some(
    (field) => field.name === 'attachmentdataCollectedFilePath',
  );
  if (!hasAttachment) return view;
  return {
    ...view,
    thumbnail_field: ATTACHMENT_PREVIEW_FIELD,
    media_kind: view.media_kind === 'image' ? 'auto' : view.media_kind,
  };
}

/** URL and MIME paths a gallery or pin preview has to select. */
export function galleryMediaFields(
  view: Pick<MobileFrontEndGalleryView, 'media_field' | 'thumbnail_field' | 'mime_field'>,
): string[] {
  const fields = [
    view.media_field,
    view.thumbnail_field,
    view.mime_field,
    collectedFileMimeField(view.thumbnail_field),
  ];
  const seen = new Set<string>();
  const selected: string[] = [];
  for (const field of fields) {
    if (!field || seen.has(field)) continue;
    seen.add(field);
    selected.push(field);
  }
  return selected;
}

/** Every field a gallery page must select. */
export function galleryFields(
  view: MobileFrontEndGalleryView,
  artifact: MobileUiConfigArtifact | undefined,
): string[] {
  const fields = new Set<string>([ARTIFACT_ID_FIELD, ...galleryMediaFields(view)]);
  for (const field of [view.caption_field, view.timestamp_field, ...(view.detail_fields ?? [])]) {
    if (field) fields.add(field);
  }
  return [...fields].filter((field) => isSelectableFieldPath(artifact, field));
}

/**
 * A `where` clause matching only rows whose media field holds a fetchable URL.
 *
 * mobile-service has no null/not-null comparator, so this leans on `contains`
 * (an ILIKE substring match) against the scheme separator. It matters because an
 * artifact type may carry media on a small fraction of its rows, and paging past
 * thousands of blanks to reach the images is the problem this view exists to
 * solve. Nested paths aren't filterable, so those fall back to client-side
 * dropping in {@link mapGalleryItems}.
 */
export function galleryMediaPresentWhere(
  view: MobileFrontEndGalleryView,
  artifact?: MobileUiConfigArtifact,
): Record<string, unknown> | null {
  if (view.media_field.includes('.')) return null;
  // `contains` is a String comparator. Sending it on a URL/JSON/enum field
  // is a GraphQL error, not an empty page.
  const kind = artifact?.capabilities?.filterable_fields?.[view.media_field];
  const meta = artifact?.fields.find((field) => field.name === view.media_field);
  const looksLikeString =
    kind === 'String' ||
    (kind == null && meta?.scalar && !meta.list && !meta.object && /string/i.test(meta.type));
  if (!looksLikeString) return null;
  return { [view.media_field]: { contains: '://' } };
}

/** One page of gallery tiles, newest first when the view declares a timestamp. */
export function buildGalleryQuery(args: {
  rootQuery: string;
  view: MobileFrontEndGalleryView;
  artifact: MobileUiConfigArtifact | undefined;
  mobileAcquisitionId: number;
  offset: number;
  limit: number;
  where?: Record<string, unknown> | null;
}): string | null {
  const { view, where } = args;
  const mediaPresent = galleryMediaPresentWhere(view, args.artifact);

  return buildPagedQuery({
    operationName: 'ArtifactGallery',
    rootQuery: args.rootQuery,
    mobileAcquisitionId: args.mobileAcquisitionId,
    offset: args.offset,
    limit: args.limit,
    fields: galleryFields(view, args.artifact),
    where: { ...(where ?? {}), ...(mediaPresent ?? {}) },
    orderBy: orderByIfSortable(view.timestamp_field, 'desc', artifactTableView(args.artifact)),
  });
}

export interface ResolvedGalleryAsset {
  /** Full asset, or the thumbnail when the original was not collected. */
  url: string;
  mime: string | null;
  kind: GalleryMediaKind;
  /** Smaller preview when one exists; otherwise the same URL as ``url``. */
  thumbnailUrl: string;
}

/**
 * File a gallery tile or map pin should show.
 *
 * The authored media field wins. When that URL is missing or only a device
 * path, the thumbnail is the file — and its own MIME is what decides the
 * player, because the path extension often names a video the bytes are not.
 */
export function resolveGalleryAsset(
  row: Record<string, unknown>,
  view: MobileFrontEndGalleryView,
): ResolvedGalleryAsset | null {
  if (!view.media_field) return null;
  const primary = displayableMediaUrl(resolvePath(row, view.media_field));
  const thumbnail = view.thumbnail_field
    ? displayableMediaUrl(resolvePath(row, view.thumbnail_field))
    : null;
  const url = primary ?? thumbnail;
  if (!url) return null;

  const mimeField =
    primary == null
      ? (collectedFileMimeField(view.thumbnail_field) ?? view.mime_field)
      : view.mime_field;
  const mime = mimeField ? asString(resolvePath(row, mimeField)) : null;
  return {
    url,
    mime,
    kind: resolveMediaKind(view.media_kind, mime, url),
    thumbnailUrl: thumbnail ?? url,
  };
}

/** Map raw rows to tiles, dropping any whose media URL didn't come back. */
export function mapGalleryItems(
  rows: Record<string, unknown>[],
  view: MobileFrontEndGalleryView,
): GalleryItem[] {
  const items: GalleryItem[] = [];

  for (const [index, row] of rows.entries()) {
    const asset = resolveGalleryAsset(row, view);
    if (!asset) continue;

    const caption = view.caption_field
      ? asString(resolvePath(row, view.caption_field))
      : null;
    const timestamp = view.timestamp_field
      ? asString(resolvePath(row, view.timestamp_field))
      : null;

    items.push({
      id: String(resolvePath(row, ARTIFACT_ID_FIELD) ?? `${index}-${asset.url}`),
      url: asset.url,
      thumbnailUrl: asset.thumbnailUrl,
      kind: asset.kind,
      caption: caption || null,
      timestamp: timestamp || null,
      row,
    });
  }

  return items;
}

function asString(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}
